import React, { useCallback, useEffect, useMemo, useState } from 'react';
import client from '../../api/client';
import HierarchyPicker from '../../components/HierarchyPicker';
import ViewModeToggle from '../../components/ViewModeToggle';
import { useViewMode } from '../../hooks/useViewMode';
import { notifyCustomersChanged } from '../../utils/cacheEvents';
import '../../components/ExpandableList.css';
import './AdminPages.css';
import './CustomerManager.css';

const PAYMENT_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-annually', 'Annually', 'One-time'];

const HEALTH_OPTIONS = [
  { value: '', label: 'Auto (from QBR / signals)' },
  { value: 'green', label: 'Green' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'red', label: 'Red' },
];

const EMPTY_EDITOR = {
  id: null,
  name: '',
  includedNodeIds: [],
  excludedNodeIds: [],
  includeChildrenByDefault: true,
  assignedUserIds: [],
  csRepUserIds: [],
  primaryUserId: null,
  dealType: 'standalone',
  parentCustomerId: null,
  contractStartDate: '',
  contractTermMonths: '',
  paymentAmount: '',
  paymentFrequency: '',
  notes: '',
  healthColorOverride: '',
};

// Qbr draft shape for the "Add QBR" modal.
const EMPTY_QBR = {
  meetingDate: new Date().toISOString().slice(0, 10),
  rating: 8,
  summary: '',
};

function formatMoney(amount, frequency) {
  if (amount == null || amount === '') return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return frequency ? `${formatted} ${frequency.toLowerCase()}` : formatted;
}

function formatRenewal(startDate, termMonths) {
  if (!startDate || !termMonths) return null;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + Number(termMonths));
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function HealthDot({ color, size = 14, title }) {
  const bg = color === 'green' ? '#10b981'
    : color === 'yellow' ? '#f59e0b'
    : color === 'red' ? '#ef4444'
    : '#cbd5e1';
  return (
    <span
      className="customer-health-dot"
      title={title || color || 'unknown'}
      style={{ width: size, height: size, background: bg }}
    />
  );
}

function withMasterFlags(list) {
  const childIds = new Set(list.filter(x => x.parentCustomerId).map(x => x.parentCustomerId));
  return list.map(x => ({ ...x, isMaster: childIds.has(x.id) }));
}

function csRepNamesFromUsers(csRepUserIds, users) {
  return (csRepUserIds || [])
    .map(uid => users.find(u => String(u.id) === String(uid)))
    .filter(Boolean)
    .map(u => [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || u.userName)
    .filter(Boolean);
}

/** Build a list-row shape from the editor after a create/update. */
function listItemFromEditor(editing, users, id = editing.id) {
  const parentCustomerId = editing.dealType === 'sub' ? editing.parentCustomerId : null;
  const override = editing.healthColorOverride || null;
  return {
    id,
    name: editing.name.trim(),
    includedNodeIds: editing.includedNodeIds || [],
    excludedNodeIds: editing.excludedNodeIds || [],
    includeChildrenByDefault: editing.includeChildrenByDefault !== false,
    assignedUserIds: editing.assignedUserIds || [],
    csRepUserIds: editing.csRepUserIds || [],
    primaryUserId: editing.primaryUserId || null,
    parentCustomerId,
    contractStartDate: editing.contractStartDate || null,
    contractTermMonths: editing.contractTermMonths === '' ? null : Number(editing.contractTermMonths),
    paymentAmount: editing.paymentAmount === '' ? null : Number(editing.paymentAmount),
    paymentFrequency: editing.paymentFrequency || null,
    notes: editing.notes?.trim() || null,
    healthColorOverride: override,
    csRepDisplayNames: csRepNamesFromUsers(editing.csRepUserIds, users),
    health: override ? { color: override } : null,
  };
}

function DealTypeBadge({ customer }) {
  if (customer.parentCustomerId) {
    return <span className="customer-badge badge-sub">Sub-deal</span>;
  }
  if (customer.isMaster) {
    return <span className="customer-badge badge-master">Master</span>;
  }
  return <span className="customer-badge badge-standalone">Standalone</span>;
}

function CustomerManager() {
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [qbrModal, setQbrModal] = useState(null);
  const [viewMode, setViewMode] = useViewMode('adminCustomers');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const applyList = useCallback((list) => {
    setCustomers(withMasterFlags(list));
  }, []);

  const loadUsers = useCallback(async () => {
    const u = await client.get('/api/admin/users').catch(() => ({ data: { users: [] } }));
    setUsers(u.data?.users || []);
  }, []);

  // Fast path: names + assignments only (no rating-engine health pass).
  const loadFast = useCallback(async () => {
    const c = await client.get('/api/customers');
    const list = c.data?.customers || [];
    applyList(list);
    return list;
  }, [applyList]);

  // Slow path: enriches health dots; runs in the background.
  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const c = await client.get('/api/customers?includeHealth=true');
      applyList(c.data?.customers || []);
    } finally {
      setHealthLoading(false);
    }
  }, [applyList]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadFast(), loadUsers()]);
      loadHealth();
    } finally {
      setLoading(false);
    }
  }, [loadFast, loadUsers, loadHealth]);

  useEffect(() => { load(); }, [load]);

  const masters = useMemo(
    () => customers.filter(c => !c.parentCustomerId),
    [customers]
  );

  const startCreate = () => setEditing({ ...EMPTY_EDITOR });

  const startEdit = (c) => {
    setEditing({
      id: c.id,
      name: c.name,
      includedNodeIds: c.includedNodeIds || [],
      excludedNodeIds: c.excludedNodeIds || [],
      includeChildrenByDefault: c.includeChildrenByDefault !== false,
      assignedUserIds: c.assignedUserIds || [],
      csRepUserIds: c.csRepUserIds || [],
      primaryUserId: c.primaryUserId || null,
      dealType: c.parentCustomerId ? 'sub' : (c.isMaster ? 'master' : 'standalone'),
      parentCustomerId: c.parentCustomerId || null,
      contractStartDate: c.contractStartDate ? c.contractStartDate.slice(0, 10) : '',
      contractTermMonths: c.contractTermMonths ?? '',
      paymentAmount: c.paymentAmount ?? '',
      paymentFrequency: c.paymentFrequency || '',
      notes: c.notes || '',
      healthColorOverride: c.healthColorOverride || '',
    });
    setDetail(null);
  };

  const save = async () => {
    if (!editing.name.trim()) return alert('Name is required');
    if (editing.dealType === 'sub' && !editing.parentCustomerId) {
      return alert('Please select a master (parent) customer for this sub-deal');
    }
    try {
      const payload = {
        name: editing.name.trim(),
        includedNodeIds: editing.includedNodeIds,
        excludedNodeIds: editing.excludedNodeIds,
        includeChildrenByDefault: editing.includeChildrenByDefault,
        assignedUserIds: editing.assignedUserIds,
        csRepUserIds: editing.csRepUserIds,
        primaryUserId: editing.primaryUserId,
        parentCustomerId: editing.dealType === 'sub' ? editing.parentCustomerId : null,
        contractStartDate: editing.contractStartDate || null,
        contractTermMonths: editing.contractTermMonths === '' ? null : Number(editing.contractTermMonths),
        paymentAmount: editing.paymentAmount === '' ? null : Number(editing.paymentAmount),
        paymentFrequency: editing.paymentFrequency || null,
        notes: editing.notes?.trim() || null,
        healthColorOverride: editing.healthColorOverride || null,
      };
      if (editing.id == null) {
        const res = await client.post('/api/customers', payload);
        const newId = res.data?.id;
        if (newId != null) {
          const row = listItemFromEditor(editing, users, newId);
          setCustomers(prev => withMasterFlags([...prev, row]));
        } else {
          await loadFast();
        }
      } else {
        await client.put(`/api/customers/${editing.id}`, payload);
        const row = listItemFromEditor(editing, users);
        setCustomers(prev => withMasterFlags(
          prev.map(c => (c.id === editing.id ? { ...c, ...row } : c))
        ));
      }
      setEditing(null);
      notifyCustomersChanged();
      loadHealth();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this customer? This does not delete facilities or users.')) return;
    try {
      await client.delete(`/api/customers/${id}`);
      setCustomers(prev => withMasterFlags(prev.filter(c => c.id !== id)));
      notifyCustomersChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  const openDetail = async (customer) => {
    try {
      const res = await client.get(`/api/customers/${customer.id}`);
      setDetail(res.data?.customer || null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load customer detail');
    }
  };

  const saveQbr = async () => {
    if (!qbrModal) return;
    const { customerId, draft, qbrId } = qbrModal;
    if (!draft.summary.trim()) return alert('Summary is required');
    if (draft.rating < 1 || draft.rating > 10) return alert('Rating must be 1-10');
    try {
      if (qbrId) {
        await client.put(`/api/customers/qbrs/${qbrId}`, draft);
      } else {
        await client.post(`/api/customers/${customerId}/qbrs`, draft);
      }
      setQbrModal(null);
      if (detail?.id === customerId) {
        const res = await client.get(`/api/customers/${customerId}`);
        setDetail(res.data?.customer || null);
      }
      loadHealth();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  };

  const deleteQbr = async (qbrId, customerId) => {
    if (!window.confirm('Delete this QBR?')) return;
    try {
      await client.delete(`/api/customers/qbrs/${qbrId}`);
      if (detail?.id === customerId) {
        const res = await client.get(`/api/customers/${customerId}`);
        setDetail(res.data?.customer || null);
      }
      loadHealth();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading && customers.length === 0) {
    return <div className="admin-loading">Loading customers…</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>Customers</h2>
          <p>
            Paying customers (deals). Assign facilities via the hierarchy picker, attach contract details, and log QBR meetings. CS reps see their assigned customers on their Home dashboard.
            {healthLoading && <span className="admin-health-refreshing"> Updating health…</span>}
          </p>
        </div>
        {!editing && !detail && (
          <div className="admin-header-actions">
            <ViewModeToggle mode={viewMode} onChange={setViewMode} />
            <button className="admin-primary-btn" onClick={startCreate}>+ New Customer</button>
          </div>
        )}
      </div>

      {editing && (
        <CustomerEditor
          editing={editing}
          setEditing={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save}
          users={users}
          masters={masters}
        />
      )}

      {detail && (
        <CustomerDetail
          customer={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { startEdit(detail); }}
          onAddQbr={() => setQbrModal({ customerId: detail.id, draft: { ...EMPTY_QBR } })}
          onEditQbr={(q) => setQbrModal({
            customerId: detail.id,
            qbrId: q.id,
            draft: {
              meetingDate: q.meetingDate.slice(0, 10),
              rating: q.rating,
              summary: q.summary
            }
          })}
          onDeleteQbr={(q) => deleteQbr(q.id, detail.id)}
        />
      )}

      {qbrModal && (
        <QbrModal
          draft={qbrModal.draft}
          isEdit={!!qbrModal.qbrId}
          onChange={(patch) => setQbrModal({ ...qbrModal, draft: { ...qbrModal.draft, ...patch } })}
          onCancel={() => setQbrModal(null)}
          onSave={saveQbr}
        />
      )}

      {!editing && !detail && (
        <div className={`customer-list ${viewMode === 'list' ? 'is-list-view' : ''}`}>
          {customers.length === 0 && (
            <div className="admin-empty-card">
              <p>No customers yet.</p>
              <button className="admin-primary-btn" onClick={startCreate}>Create your first customer</button>
            </div>
          )}
          {viewMode === 'list' ? (
            <div className="expandable-list customer-expandable-list">
              {customers.map(c => {
                const isExpanded = expandedIds.has(c.id);
                return (
                  <div
                    key={c.id}
                    className={`expandable-list-item ${isExpanded ? 'is-expanded' : ''}`}
                  >
                    <div
                      className="expandable-list-row"
                      onClick={() => toggleExpanded(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded(c.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <button
                        type="button"
                        className="expandable-list-expand"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        onClick={(e) => { e.stopPropagation(); toggleExpanded(c.id); }}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                      <div className="expandable-list-title">
                        <HealthDot color={c.health?.color} size={18} title={`Health: ${c.health?.color || 'unknown'}`} />
                        <span className="expandable-list-title-text">{c.name}</span>
                      </div>
                      <div className="expandable-list-row-meta">
                        <DealTypeBadge customer={c} />
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="expandable-list-details">
                        <CustomerCardSummary
                          customer={c}
                          onOpenDetail={() => openDetail(c)}
                          onEdit={() => startEdit(c)}
                          onDelete={() => remove(c.id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            customers.map(c => (
              <div key={c.id} className="customer-card" onClick={() => openDetail(c)}>
                <CustomerCardSummary
                  customer={c}
                  onEdit={() => startEdit(c)}
                  onDelete={() => remove(c.id)}
                  showHead
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CustomerCardSummary({ customer, onEdit, onDelete, onOpenDetail, showHead = false }) {
  return (
    <>
      {showHead && (
        <div className="customer-card-head">
          <HealthDot color={customer.health?.color} size={18} title={`Health: ${customer.health?.color || 'unknown'}`} />
          <div className="customer-card-title">
            <h3 title={customer.name}>{customer.name}</h3>
            <DealTypeBadge customer={customer} />
          </div>
          <div className="admin-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" onClick={onEdit}>Edit</button>
            <button type="button" className="admin-danger" onClick={onDelete}>Delete</button>
          </div>
        </div>
      )}
      {!showHead && (
        <div className="customer-card-actions-row">
          <div className="admin-actions">
            <button type="button" onClick={onEdit}>Edit</button>
            <button type="button" className="admin-danger" onClick={onDelete}>Delete</button>
          </div>
          {onOpenDetail && (
            <button type="button" className="admin-secondary-btn customer-open-detail-btn" onClick={onOpenDetail}>
              View full detail →
            </button>
          )}
        </div>
      )}
      <div className="customer-card-meta">
        <span>{customer.includedNodeIds?.length || 0} nodes picked</span>
        {customer.excludedNodeIds?.length > 0 && <span>{customer.excludedNodeIds.length} excluded</span>}
        <span>{customer.assignedUserIds?.length || 0} CS rep{customer.assignedUserIds?.length === 1 ? '' : 's'}</span>
        {formatMoney(customer.paymentAmount, customer.paymentFrequency) && (
          <span className="customer-money">{formatMoney(customer.paymentAmount, customer.paymentFrequency)}</span>
        )}
        {formatRenewal(customer.contractStartDate, customer.contractTermMonths) && (
          <span className="admin-muted">Renews {formatRenewal(customer.contractStartDate, customer.contractTermMonths)}</span>
        )}
      </div>
      {customer.health?.signals?.length > 0 && (
        <ul className="customer-list-health-signals">
          {customer.health.signals.slice(0, 4).map((s, i) => (
            <li key={i}>
              <HealthDot color={s.color} size={8} />
              <span>{s.name}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function CustomerEditor({ editing, setEditing, onCancel, onSave, users, masters }) {
  const patch = (p) => setEditing({ ...editing, ...p });

  const eligibleMasters = masters.filter(m => m.id !== editing.id);

  return (
    <div className="admin-group-editor customer-editor">
      <div className="admin-group-editor-header">
        <input
          placeholder="Name (e.g., HCA — Tristar Division)"
          value={editing.name}
          onChange={e => patch({ name: e.target.value })}
          autoFocus
        />
        <label className="admin-inline-check">
          <input
            type="checkbox"
            checked={editing.includeChildrenByDefault}
            onChange={e => patch({ includeChildrenByDefault: e.target.checked })}
          />
          Include descendants by default
        </label>
      </div>

      <section className="customer-section">
        <h4>Deal type</h4>
        <div className="customer-deal-type">
          {['standalone', 'master', 'sub'].map(opt => (
            <label key={opt} className={`admin-select-chip ${editing.dealType === opt ? 'selected' : ''}`}>
              <input
                type="radio"
                name="dealType"
                checked={editing.dealType === opt}
                onChange={() => patch({ dealType: opt, parentCustomerId: opt === 'sub' ? editing.parentCustomerId : null })}
              />
              {opt === 'standalone' && 'Standalone'}
              {opt === 'master' && 'Master (has sub-deals)'}
              {opt === 'sub' && 'Sub-deal of…'}
            </label>
          ))}
          {editing.dealType === 'sub' && (
            <select
              value={editing.parentCustomerId || ''}
              onChange={e => patch({ parentCustomerId: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">Select master…</option>
              {eligibleMasters.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
        </div>
        {editing.dealType === 'master' && (
          <p className="admin-form-hint">
            Master customers roll up health from their sub-deals and union all facilities across them.
          </p>
        )}
      </section>

      <section className="customer-section">
        <h4>Facilities</h4>
        <HierarchyPicker
          value={{
            includedNodeIds: editing.includedNodeIds,
            excludedNodeIds: editing.excludedNodeIds,
            includeChildrenByDefault: editing.includeChildrenByDefault
          }}
          onChange={v => patch({
            includedNodeIds: v.includedNodeIds,
            excludedNodeIds: v.excludedNodeIds
          })}
        />
      </section>

      <section className="customer-section">
        <h4>Contract <span className="admin-muted">(optional)</span></h4>
        <div className="customer-contract-grid">
          <label className="admin-field">
            <span>Start date</span>
            <input
              type="date"
              value={editing.contractStartDate}
              onChange={e => patch({ contractStartDate: e.target.value })}
            />
          </label>
          <label className="admin-field">
            <span>Term (months)</span>
            <input
              type="number"
              min="0"
              placeholder="e.g. 60"
              value={editing.contractTermMonths}
              onChange={e => patch({ contractTermMonths: e.target.value })}
            />
          </label>
          <label className="admin-field">
            <span>Payment amount ($)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 5000"
              value={editing.paymentAmount}
              onChange={e => patch({ paymentAmount: e.target.value })}
            />
          </label>
          <label className="admin-field">
            <span>Frequency</span>
            <select
              value={editing.paymentFrequency}
              onChange={e => patch({ paymentFrequency: e.target.value })}
            >
              <option value="">—</option>
              {PAYMENT_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
        </div>
        <label className="admin-field">
          <span>Notes</span>
          <textarea
            rows={3}
            placeholder="Any context about the deal (terms, stakeholders, renewal notes…)"
            value={editing.notes}
            onChange={e => patch({ notes: e.target.value })}
          />
        </label>
      </section>

      <section className="customer-section">
        <h4>Health</h4>
        <div className="customer-health-row">
          <label className="admin-field" style={{ maxWidth: 320 }}>
            <span>Color override</span>
            <select
              value={editing.healthColorOverride}
              onChange={e => patch({ healthColorOverride: e.target.value })}
            >
              {HEALTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <p className="admin-form-hint">
            Leave on <strong>Auto</strong> to let the latest QBR rating drive the color
            (rating 8+ green, 5–7 yellow, &lt;5 red). Masters roll up the worst of their sub-deals.
          </p>
        </div>
      </section>

      <section className="customer-section">
        <h4>Assigned users</h4>
        <p className="admin-form-hint">
          Anyone selected here will see this customer on their Home dashboard.
          Use <strong>Acts as CS rep</strong> to control whose name shows in the
          customer card's rep block — admins assigned only for visibility stay
          off the card. Marking a user <strong>Primary</strong> automatically
          flags them as a CS rep.
        </p>
        <div className="admin-chips-select">
          {users.length === 0 && <em>No users loaded.</em>}
          {users.map(u => {
            const selected = editing.assignedUserIds.includes(u.id);
            const isPrimary = editing.primaryUserId === u.id;
            const isCsRep = editing.csRepUserIds.includes(u.id) || isPrimary;
            const userIsCsRepRole = u.role === 'CSRep';
            return (
              <label key={u.id} className={`admin-select-chip ${selected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => {
                    if (selected) {
                      patch({
                        assignedUserIds: editing.assignedUserIds.filter(x => x !== u.id),
                        csRepUserIds: editing.csRepUserIds.filter(x => x !== u.id),
                        primaryUserId: isPrimary ? null : editing.primaryUserId,
                      });
                    } else {
                      patch({
                        assignedUserIds: [...editing.assignedUserIds, u.id],
                        csRepUserIds: userIsCsRepRole
                          ? [...new Set([...editing.csRepUserIds, u.id])]
                          : editing.csRepUserIds,
                      });
                    }
                  }}
                />
                <span>{[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</span>
                <span className="admin-muted" style={{ fontSize: '0.7rem' }}>{u.role}</span>
                {selected && (
                  <>
                    <button
                      type="button"
                      className={`admin-primary-toggle ${isCsRep ? 'active' : ''}`}
                      disabled={isPrimary}
                      onClick={(e) => {
                        e.preventDefault();
                        const has = editing.csRepUserIds.includes(u.id);
                        patch({
                          csRepUserIds: has
                            ? editing.csRepUserIds.filter(x => x !== u.id)
                            : [...editing.csRepUserIds, u.id],
                        });
                      }}
                      title={isPrimary ? 'Primary always acts as CS rep' : 'Show this user on the customer card'}
                    >
                      {isCsRep ? '✓ acts as CS rep' : '+ acts as CS rep'}
                    </button>
                    <button
                      type="button"
                      className={`admin-primary-toggle ${isPrimary ? 'active' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const nextPrimary = isPrimary ? null : u.id;
                        const nextReps = nextPrimary && !editing.csRepUserIds.includes(u.id)
                          ? [...editing.csRepUserIds, u.id]
                          : editing.csRepUserIds;
                        patch({ primaryUserId: nextPrimary, csRepUserIds: nextReps });
                      }}
                      title="Toggle primary CS rep"
                    >
                      {isPrimary ? '★ primary' : '☆ make primary'}
                    </button>
                  </>
                )}
              </label>
            );
          })}
        </div>
      </section>

      <div className="admin-modal-actions">
        <button onClick={onCancel}>Cancel</button>
        <button className="admin-primary-btn" onClick={onSave}>
          {editing.id == null ? 'Create customer' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function CustomerDetail({ customer, onClose, onEdit, onAddQbr, onEditQbr, onDeleteQbr }) {
  const money = formatMoney(customer.paymentAmount, customer.paymentFrequency);
  const renewal = formatRenewal(customer.contractStartDate, customer.contractTermMonths);
  return (
    <div className="customer-detail">
      <div className="customer-detail-head">
        <button className="admin-secondary-btn" onClick={onClose}>← Back to list</button>
        <div className="customer-detail-title">
          <HealthDot color={customer.health?.color} size={22} />
          <h2>{customer.name}</h2>
        </div>
        <button className="admin-primary-btn" onClick={onEdit}>Edit</button>
      </div>

      <div className="customer-detail-grid">
        <div className="customer-detail-card">
          <h4>Contract</h4>
          {(money || renewal || customer.contractStartDate || customer.notes)
            ? (
              <ul className="customer-kv">
                {money && <li><strong>Payment</strong><span>{money}</span></li>}
                {customer.contractStartDate && (
                  <li>
                    <strong>Start</strong>
                    <span>{new Date(customer.contractStartDate).toLocaleDateString()}</span>
                  </li>
                )}
                {customer.contractTermMonths != null && (
                  <li><strong>Term</strong><span>{customer.contractTermMonths} months</span></li>
                )}
                {renewal && <li><strong>Renews</strong><span>{renewal}</span></li>}
                {customer.notes && <li className="customer-kv-notes"><strong>Notes</strong><span>{customer.notes}</span></li>}
              </ul>
            )
            : <p className="admin-muted">No contract details yet.</p>
          }
        </div>

        <div className="customer-detail-card">
          <h4>Health signals</h4>
          <div className="customer-health-summary">
            <HealthDot color={customer.health?.color} size={24} />
            <div>
              <strong>{customer.health?.color || 'unknown'}</strong>
              <div className="admin-muted">source: {customer.health?.source}</div>
            </div>
          </div>
          <ul className="customer-signals">
            {(customer.health?.signals || []).map((s, i) => (
              <li key={i}>
                <HealthDot color={s.color} size={10} />
                <span>{s.name}</span>
                {s.note && <em>{s.note}</em>}
              </li>
            ))}
            {(!customer.health?.signals || customer.health.signals.length === 0) && (
              <li className="admin-muted">No signals yet.</li>
            )}
          </ul>
        </div>
      </div>

      <div className="customer-detail-card qbr-card">
        <div className="qbr-card-head">
          <h4>QBR meetings</h4>
          <button className="admin-primary-btn" onClick={onAddQbr}>+ Add QBR</button>
        </div>
        {(!customer.qbrs || customer.qbrs.length === 0) && (
          <p className="admin-muted">No QBRs logged yet.</p>
        )}
        <ul className="qbr-list">
          {(customer.qbrs || []).map(q => (
            <li key={q.id} className="qbr-item">
              <div className="qbr-item-head">
                <span className="qbr-date">{new Date(q.meetingDate).toLocaleDateString()}</span>
                <span className={`qbr-rating rating-${q.rating >= 8 ? 'good' : q.rating >= 5 ? 'ok' : 'bad'}`}>
                  {q.rating}/10
                </span>
                <div className="admin-actions">
                  <button onClick={() => onEditQbr(q)}>Edit</button>
                  <button className="admin-danger" onClick={() => onDeleteQbr(q)}>Delete</button>
                </div>
              </div>
              <p className="qbr-summary">{q.summary}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function QbrModal({ draft, isEdit, onChange, onCancel, onSave }) {
  return (
    <div className="admin-modal-backdrop">
      <div className="admin-modal">
        <h3>{isEdit ? 'Edit QBR' : 'Add QBR'}</h3>
        <label className="admin-field">
          <span>Meeting date</span>
          <input
            type="date"
            value={draft.meetingDate}
            onChange={e => onChange({ meetingDate: e.target.value })}
          />
        </label>
        <label className="admin-field">
          <span>Rating: <strong>{draft.rating}</strong> / 10</span>
          <input
            type="range"
            min="1"
            max="10"
            value={draft.rating}
            onChange={e => onChange({ rating: Number(e.target.value) })}
          />
        </label>
        <label className="admin-field">
          <span>Summary</span>
          <textarea
            rows={6}
            placeholder="How did the meeting go? Wins, concerns, action items…"
            value={draft.summary}
            onChange={e => onChange({ summary: e.target.value })}
          />
        </label>
        <div className="admin-modal-actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="admin-primary-btn" onClick={onSave}>
            {isEdit ? 'Save changes' : 'Add QBR'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomerManager;
